/**
 * Small readiness primitives shared by the calendar chips, the detail drawer
 * and the drilldown Closing tab: the level badge, the funding-signal dot, the
 * attention chips, and a generic tinted pill.
 */
import { useCallback, useState } from "react";

import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import { Box, Tooltip } from "@mui/material";

import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import { slate, teal } from "../../theme/colors";
import { FONT_MONO, TYPE } from "./typography";
import {
  cardAttentionChips,
  readinessMeta,
  signalColor,
} from "./closingReadinessUtils";

// Mono stack for counts / chips in section headers (see typography.js).
export const MONO = FONT_MONO;

// Generic tinted pill. `variant="tag"` is the squarer 5px-radius chip used
// inside checklist rows (owner role, "optional"); the default is the 99px
// header pill.
export const Pill = ({ label, c, bg, size = "sm", sx, title, variant = "pill" }) => {
  const tag = variant === "tag";
  const el = (
    <Flx
      ac
      sx={{
        px: tag ? 0.875 : size === "xs" ? 1.125 : 1.25,
        py: tag ? "2px" : size === "xs" ? "3px" : 0.5,
        borderRadius: tag ? "5px" : "99px",
        background: bg,
        color: c,
        // type/micro — the floor: chips never render below 10px.
        ...TYPE.micro,
        fontWeight: 600,
        whiteSpace: "nowrap",
        flexShrink: 0,
        ...sx,
      }}
    >
      {label}
    </Flx>
  );
  return title ? (
    <Tooltip title={title} arrow>
      {el}
    </Tooltip>
  ) : (
    el
  );
};

// Solid colour dot for the Capital Markets funding signal (red/amber/green).
export const SignalDot = ({ signal, size = 8 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: "99px",
      background: signalColor(signal),
      flexShrink: 0,
    }}
  />
);

// Readiness level badge: dot + level label, both in the LEVEL colour so the
// dot always agrees with the pill it sits in (Not scheduled is magenta, not
// the Capital Markets red). `signal` is accepted for callers that pass it but
// no longer drives the dot.
export const ReadinessBadge = ({ level, signal, size = "sm", sx }) => {
  const m = readinessMeta(level);
  return (
    <Flx
      ac
      g={0.6}
      sx={{
        px: 1,
        py: size === "xs" ? 0.2 : 0.4,
        borderRadius: "99px",
        background: m.bg,
        color: m.c,
        flexShrink: 0,
        ...sx,
      }}
    >
      <Box
        sx={{
          width: size === "xs" ? 6 : 7,
          height: size === "xs" ? 6 : 7,
          borderRadius: "99px",
          background: m.c,
          flexShrink: 0,
        }}
      />
      <Txt
        sx={{ ...TYPE.micro, color: m.c, whiteSpace: "nowrap" }}
      >
        {m.label}
      </Txt>
    </Flx>
  );
};

// The at-a-glance flags (stale, ECD churn, missed, date passed, …).
export const AttentionChips = ({ card, size = "xs", max }) => {
  const chips = cardAttentionChips(card);
  const shown = max ? chips.slice(0, max) : chips;
  if (!shown.length) return null;
  return (
    <Flx ac wrap g={0.5}>
      {shown.map((ch) => (
        <Pill key={ch.key} label={ch.label} c={ch.c} bg={ch.bg} size={size} />
      ))}
      {max && chips.length > max && (
        <Pill
          label={`+${chips.length - max}`}
          c={slate[600]}
          bg={slate[100]}
          size={size}
        />
      )}
    </Flx>
  );
};

// Section header used by the detail blocks. Pass `open` + `onToggle` to make
// it a collapse control (chevron, whole title row clickable); the `right` slot
// stays interactive without toggling.
export const BlockTitle = ({ children, right, sx, open, onToggle }) => {
  const collapsible = typeof onToggle === "function";
  return (
    <Flx
      ac
      jb
      g={1}
      onClick={collapsible ? onToggle : undefined}
      sx={{
        mb: open === false ? 0 : 1,
        cursor: collapsible ? "pointer" : "default",
        userSelect: collapsible ? "none" : "auto",
        borderRadius: "6px",
        mx: collapsible ? -0.5 : 0,
        px: collapsible ? 0.5 : 0,
        "&:hover": collapsible ? { background: slate[50] } : {},
        ...sx,
      }}
    >
      <Flx ac g={0.5} sx={{ minWidth: 0 }}>
        {collapsible && (
          <KeyboardArrowDownRounded
            sx={{
              fontSize: 16,
              color: slate[400],
              transition: "transform 0.15s",
              transform: open === false ? "rotate(-90deg)" : "none",
              flexShrink: 0,
            }}
          />
        )}
        <Txt sx={TYPE.sectionH}>{children}</Txt>
      </Flx>
      {right != null && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
            // Every chip / count in a header row shares the mono stack.
            fontFamily: MONO,
            "& *": { fontFamily: MONO },
          }}
        >
          {right}
        </Box>
      )}
    </Flx>
  );
};

// type/meta — helper lines, second lines, attributions.
export const Muted = ({ children, sx }) => <Txt sx={{ ...TYPE.meta, ...sx }}>{children}</Txt>;

// "Collapse all" / "Expand all" text button. `allOpen` says which one to show
// (the last broadcast state); `onSet(open)` fires the broadcast. Hosts keep
// `{ version, open }` in state and provide it via CollapseAllContext.
export const CollapseAllButton = ({ allOpen, onSet, sx }) => (
  <Txt
    onClick={(e) => {
      e.stopPropagation();
      onSet(!allOpen);
    }}
    sx={{
      // A soft teal pill in the active-text colour — visible, not loud.
      ...TYPE.micro,
      fontWeight: 600,
      color: teal[700],
      background: teal[100],
      cursor: "pointer",
      userSelect: "none",
      whiteSpace: "nowrap",
      px: 1.5,
      py: "4px",
      borderRadius: "99px",
      lineHeight: 1.5,
      transition: "background 0.13s",
      "&:hover": { background: teal[200] },
      ...sx,
    }}
  >
    {allOpen ? "Collapse all" : "Expand all"}
  </Txt>
);

// The host-side state for the broadcast: returns [ctxValue, setAll(open),
// focusSection(id)]. `initialOpen` is what sections start as (and what the
// button label reflects) before any broadcast; a section can still override it
// with its own `defaultOpen`. `focusSection(id)` opens ONE Collapsible (the one
// mounted with that `id`) without touching the others — the deep link from the
// blocker callout and from "detail in Documents".
export const useCollapseAllState = ({ initialOpen = true } = {}) => {
  const [state, setState] = useState({
    version: 0,
    open: initialOpen,
    defaultOpen: initialOpen,
    focus: { id: null, version: 0 },
  });
  const setAll = useCallback(
    (open) => setState((s) => ({ ...s, version: s.version + 1, open })),
    [],
  );
  const focusSection = useCallback(
    (id) => setState((s) => ({ ...s, focus: { id, version: s.focus.version + 1 } })),
    [],
  );
  return [state, setAll, focusSection];
};
